import { contactRows, contactsUnreadyMessage } from '@/lib/contactsRead';
import { useState, useMemo } from 'react';
import { Plus } from 'lucide-react';
import {
  ExpenditureCategory,
  ContactType,
  isInsufficientFederalPurpose,
} from '@signalvote/shared';
import type { CreateExpenditureRequest, CreateFinanceContactRequest } from '@signalvote/shared';
import { useCreateExpenditure } from '@/hooks/queries/useFinanceExpenditures';
import { useFinanceContacts, useCreateFinanceContact } from '@/hooks/queries/useFinanceContacts';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useMdExpensePurposeCascade } from '@/hooks/queries/useFinanceRulesets';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { InlineContactForm } from '@/components/finance/entry/entryParts';
import { generalExpenditureCategoryOptions } from '@/components/finance/classification/expenditureCategoryOptions';
import { MdExpensePurposePicker } from '@/components/finance/entry/MdExpensePurposePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ContactCombobox } from '@/components/finance/shared/classification';
import { SettlesDebtPicker } from '@/components/finance/debts';
import { RepaysLoanPicker } from '@/components/finance/loans';
import { readyProfile, profileUnreadyMessage } from '@/lib/complianceProfileRead';
import { answered } from '@/lib/reportsRead';
import { unreadyMessage } from '@/lib/financeRemainderReads';
import { skip } from '@signalvote/data';

interface AddExpenditureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * BSW-004: optional link to a raw bank line. Plumbed through to the create
   * mutation. See <code>AddContributionDialog</code> for the same semantics.
   */
  bankTransactionId?: string | null;
}

const CREATE_NEW = '__create_new__';

export function AddExpenditureDialog({ open, onOpenChange, bankTransactionId = null }: AddExpenditureDialogProps) {
  const { mutateAsync: createExpenditure, isPending } = useCreateExpenditure();
  const { mutateAsync: createContact, isPending: isCreatingContact } = useCreateFinanceContact();
  const contactsRead = useFinanceContacts({ pageSize: 100 });
  const contactItems = contactRows(contactsRead);
  const contactsUnready = contactsUnreadyMessage(contactsRead);
  const profileRead = useComplianceProfile();
  const profile = readyProfile(profileRead);
  const profileUnready = profileUnreadyMessage(profileRead);
  // Jurisdiction-aware category options from the one shared helper its profile-aware siblings
  // (ExpenditurePathBody, TriageLedger) already use — restores the Transfer/Reimbursement
  // categories this dialog used to drop and applies AL-specific labels, instead of a hardcoded
  // list that diverges from the rest of the app.
  const categoryOptions = useMemo(
    () => generalExpenditureCategoryOptions(profile?.stateCode),
    [profile?.stateCode],
  );
  // Maryland (MDCRIS, ADR-073) — the coded Category → Purpose cascade for the searchable picker.
  // Only fetched for a Maryland campaign; the hook returns nothing (and the picker is hidden)
  // elsewhere.
  const isMaryland = (profile?.stateCode ?? '').toUpperCase() === 'MD';
  const mdCascadeRead = useMdExpensePurposeCascade(isMaryland && profile?.stateCode ? profile.stateCode : skip);
  const mdCascade = answered(mdCascadeRead);
  const mdCascadeUnready = unreadyMessage(mdCascadeRead, 'the Maryland expense purposes');
  const offersEventDates = profile?.offersExpenditureEventDates === true;

  const [contactId, setContactId] = useState('');
  const [selectedContactName, setSelectedContactName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => getCampaignTodayString());
  const [authorizedDate, setAuthorizedDate] = useState('');
  const [incurredDate, setIncurredDate] = useState('');
  const [category, setCategory] = useState<ExpenditureCategory>(ExpenditureCategory.Operating);
  const [purpose, setPurpose] = useState('');
  // Maryland (MDCRIS) coded pair — null/null until the treasurer classifies (the unclassified state
  // files under the Other Expenses / Other default).
  const [mdCategory, setMdCategory] = useState<string | null>(null);
  const [mdPurpose, setMdPurpose] = useState<string | null>(null);
  // ADR-031 — optional debt this expenditure settles (null = not a debt payment).
  const [debtId, setDebtId] = useState<string | null>(null);
  const [loanId, setLoanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Inline contact creation state
  const [creatingInline, setCreatingInline] = useState(false);

  const parsedAmount = parseFloat(amount);
  // ULL-003 (D3): the structural floor is Payee + Amount > 0 + Date. Purpose is
  // no longer required — a floor-only entry saves as a draft that the ledger
  // surfaces as NeedsAction ("Add payee info") until itemizable. Date is not in
  // the guard because it defaults to today (getCampaignTodayString) and is never
  // cleared, so it is always present.
  const canSave = contactId !== '' && !isNaN(parsedAmount) && parsedAmount > 0 && !isPending;

  // The entry-time purpose nudge (federal). 11 CFR 104.3(b)(3)(i) attaches the purpose
  // requirement to disbursements that ITEMIZE, so this asks the same question the filing
  // check asks — just early enough to fix at the keystroke instead of at the file button.
  //
  // The test is this ROW's own amount against the threshold, which the filing check
  // deliberately does NOT use (it reads the payee's election-cycle aggregate, because several
  // sub-threshold rows to one payee itemize together). That difference is not a bug and must
  // not be "fixed" by copying the aggregate down here: at entry the payee's cycle total isn't
  // known, and the row test is sound in the safe direction — a single row over the threshold
  // guarantees that payee itemizes, so this can under-warn but never over-warn. The rows it
  // misses are caught later by the ledger mark and the preflight, which do know the aggregate.
  const isFederal = (profile?.level ?? '').toLowerCase() === 'federal';
  const itemizationThreshold = profile?.itemizationThreshold ?? 0;
  const purposeNeedsWork =
    isFederal
    && itemizationThreshold > 0
    && parsedAmount > itemizationThreshold
    && isInsufficientFederalPurpose(purpose);

  // Sort contacts: Payees first, then others
  const contacts = useMemo(() => {
    const items = contactItems;
    return [...items].sort((a, b) => {
      if (a.contactType === ContactType.Payee && b.contactType !== ContactType.Payee) return -1;
      if (a.contactType !== ContactType.Payee && b.contactType === ContactType.Payee) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [contactItems]);

  const resetInlineForm = () => setCreatingInline(false);

  const resetForm = () => {
    setContactId('');
    setSelectedContactName('');
    setAmount('');
    setDate(getCampaignTodayString());
    setAuthorizedDate('');
    setIncurredDate('');
    setCategory(ExpenditureCategory.Operating);
    setPurpose('');
    setMdCategory(null);
    setMdPurpose(null);
    setDebtId(null);
    setError(null);
    resetInlineForm();
  };

  const handleContactChange = (value: string) => {
    if (value === CREATE_NEW) {
      setCreatingInline(true);
    } else {
      setContactId(value);
      const contact = contacts.find((c) => c.id === value);
      setSelectedContactName(contact?.name ?? '');
    }
  };

  const handleInlineContactSave = async (request: CreateFinanceContactRequest) => {
    try {
      const result = await createContact(request);
      setContactId(result.contact.id);
      setSelectedContactName(result.contact.name);
      resetInlineForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);

    const request: CreateExpenditureRequest = {
      contactId,
      amount: parsedAmount,
      date,
      category,
      purpose: purpose.trim(),
      // Maryland only — send the coded pair (or null/null for unclassified). The picker can only
      // hold a legal pair; the server re-validates it against the allow-list.
      mdExpenseCategory: isMaryland ? mdCategory : null,
      mdExpensePurpose: isMaryland ? mdPurpose : null,
      authorizedDate: offersEventDates && authorizedDate ? authorizedDate : null,
      incurredDate: offersEventDates && incurredDate ? incurredDate : null,
      bankTransactionId,
      debtId,
      loanId,
    };

    try {
      await createExpenditure(request);
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create expenditure');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-expenditure-dialog">
        <DialogHeader>
          <DialogTitle>Add Expenditure</DialogTitle>
        </DialogHeader>
        {profileUnready && (
          <p role="alert" className="text-sm text-destructive">
            {profileUnready} The categories and itemization rules come from it.
          </p>
        )}

        <div className="space-y-3">
          {/* Payee selection or inline creation */}
          {creatingInline ? (
            <InlineContactForm
              contactType={ContactType.Payee}
              showEmployerOccupation={false}
              supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
              isSaving={isCreatingContact}
              onSave={handleInlineContactSave}
              onCancel={resetInlineForm}
            />
          ) : contactId && selectedContactName ? (
            <div>
              <label className="block text-sm font-medium mb-1">Payee *</label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{selectedContactName}</span>
                <button
                  type="button"
                  onClick={() => { setContactId(''); setSelectedContactName(''); }}
                  className="text-xs text-primary hover:underline"
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="exp-payee" className="block text-sm font-medium mb-1">Payee *</label>
              <ContactCombobox
                id="exp-payee"
                value={contactId}
                onChange={handleContactChange}
                contacts={contacts}
                unready={contactsUnready}
                placeholder="Select a payee..."
                searchPlaceholder="Search payees..."
                emptyText="No payees found."
              />
              <button
                type="button"
                onClick={() => handleContactChange(CREATE_NEW)}
                className="mt-1 flex items-center gap-1 text-xs text-primary underline"
              >
                <Plus className="h-3 w-3" />
                Create new payee
              </button>
            </div>
          )}

          <div>
            <label htmlFor="exp-amount" className="block text-sm font-medium mb-1">Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
              <input
                id="exp-amount"
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9.]/g, '');
                  setAmount(val);
                }}
                onBlur={() => {
                  const num = parseFloat(amount);
                  if (!isNaN(num)) setAmount(num.toFixed(2));
                }}
                className="w-full rounded-md border pl-7 pr-3 py-2 text-sm"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label htmlFor="exp-date" className="block text-sm font-medium mb-1">Date *</label>
            <input
              id="exp-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            />
          </div>

          {offersEventDates && (
            // A pre-election expenditure notice is owed if the payment or either of these falls in the window.
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label htmlFor="exp-authorized-date" className="block text-sm font-medium mb-1">Date authorized</label>
                <input
                  id="exp-authorized-date"
                  type="date"
                  value={authorizedDate}
                  onChange={(e) => setAuthorizedDate(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label htmlFor="exp-incurred-date" className="block text-sm font-medium mb-1">Date incurred</label>
                <input
                  id="exp-incurred-date"
                  type="date"
                  value={incurredDate}
                  onChange={(e) => setIncurredDate(e.target.value)}
                  className="w-full rounded-md border px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div>
            <label htmlFor="exp-category" className="block text-sm font-medium mb-1">Category *</label>
            <select
              id="exp-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ExpenditureCategory)}
              className="w-full rounded-md border px-3 py-2 text-sm"
            >
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="exp-purpose" className="block text-sm font-medium mb-1">Purpose</label>
            <input
              id="exp-purpose"
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm"
              placeholder="Description of expenditure purpose"
              aria-describedby={purposeNeedsWork ? 'exp-purpose-advisory' : undefined}
            />
            {purposeNeedsWork && (
              // Advice, never a gate — Save stays enabled (ULL-003 D3 keeps the floor at
              // Payee + Amount + Date so a half-known expense can still be parked as a draft).
              // Jurisdiction-neutral wording: this dialog renders for every filer.
              <p
                id="exp-purpose-advisory"
                data-testid="exp-purpose-advisory"
                className="mt-1 text-xs text-amber-700"
              >
                This one will be listed on the report, so the purpose has to say what the money
                bought — &ldquo;media&rdquo;, &ldquo;salary&rdquo;, &ldquo;polling&rdquo; and
                &ldquo;catering costs&rdquo; all work. You can still save and fill it in later.
              </p>
            )}
          </div>

          {/* Maryland (MDCRIS, ADR-073) — the coded Category → Purpose classification MDCRIS files on.
              Distinct from the jurisdiction-neutral Category/Purpose above; shown only for a Maryland
              campaign. Unclassified is fine — it files under the Other Expenses / Other default. */}
          {isMaryland && (
            <div className="rounded-md border bg-muted/30 p-3">
              <MdExpensePurposePicker
                cascade={mdCascade ?? []}
                category={mdCategory}
                purpose={mdPurpose}
                onChange={(c, p) => { setMdCategory(c); setMdPurpose(p); }}
              />
              {mdCascadeUnready && (
                <p role="alert" className="mt-2 text-sm text-destructive">{mdCascadeUnready}</p>
              )}
            </div>
          )}

          {/* ADR-031 — tag this as a debt payment (renders nothing when there are no debts). */}
          <SettlesDebtPicker
            value={debtId}
            onChange={(next) => {
              setDebtId(next);
              if (next !== null) setLoanId(null);
            }}
            id="exp-settles-debt"
          />
          {/* One payment settles one obligation, so choosing a loan clears any debt link (and
              vice versa) rather than letting the server reject the pair after the fact. */}
          <RepaysLoanPicker
            value={loanId}
            onChange={(next) => {
              setLoanId(next);
              if (next !== null) setDebtId(null);
            }}
            id="exp-repays-loan"
          />

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
